import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function Chip({ children, tone }) {
  const colors = {
    red: "bg-red-500/20 text-red-300",
    green: "bg-green-500/20 text-green-300",
    purple: "bg-purple-500/20 text-purple-300",
    default: "bg-white/10 text-white/80",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${colors[tone] || colors.default}`}>
      {children}
    </span>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [avatarUrl, setAvatarUrl] = useState(user.profile?.avatarUrl || "");
  const [role, setRole] = useState(user.role || "STUDENT");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function handleSave() {
    setBusy(true);
    setErr("");
    try {
      await adminFetch(`/api/admin/users/${user.id}`, {
        method: "PATCH",
        body: { name, avatarUrl, role },
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.message || "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl bg-[#1e2336] ring-1 ring-white/10 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-white text-lg">Edit user</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="text-sm text-white/50">{user.email} · {user.id}</div>

        {/* Avatar preview */}
        <div className="flex items-center gap-4">
          {avatarUrl ? (
            <img crossOrigin="anonymous" src={avatarUrl} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-white/20" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="h-14 w-14 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white/30">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
              </svg>
            </div>
          )}
          <div className="flex-1 text-sm text-white/60">Avatar preview</div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-white/50 mb-1">Avatar URL</label>
            <input
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-[#7269e3]"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-[#7269e3]"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-[#7269e3]"
            >
              <option value="STUDENT">STUDENT</option>
              <option value="TUTOR">TUTOR</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
        </div>

        {err && <div className="text-sm text-red-300">{err}</div>}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/15">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            className="rounded-lg bg-[#7269e3] px-4 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("ALL");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editUser, setEditUser] = useState(null);

  const pageSize = 20;
  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (role !== "ALL") params.set("role", role);
    return params.toString();
  }, [q, role, page]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/users?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load users");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function toggleBan(user) {
    const reason = user.isBanned ? "Admin unban" : prompt("Ban reason?");
    if (!reason) return;
    try {
      const endpoint = user.isBanned ? "unban" : "ban";
      await adminFetch(`/api/admin/users/${user.id}/${endpoint}`, {
        method: "POST",
        body: { isBanned: !user.isBanned, reason },
      });
      await load();
    } catch (e) {
      alert(e?.message || "Failed");
    }
  }

  async function invalidateTokens(user) {
    if (!window.confirm(`Invalidate all sessions for ${user.email}?`)) return;
    try {
      await adminFetch(`/api/admin/users/${user.id}/invalidate-tokens`, {
        method: "POST",
        body: { reason: "admin invalidate" },
      });
      alert("Tokens invalidated");
    } catch (e) {
      alert(e?.message || "Failed");
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-10 text-white">
      {editUser && (
        <EditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={load}
        />
      )}

      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-unbounded text-3xl font-bold">Admin · Users</h1>
          <p className="mt-2 text-white/70">Search, edit name/avatar/role, ban/unban, invalidate tokens.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name/email…"
            className="w-full md:w-64 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />
          <select
            value={role}
            onChange={(e) => { setRole(e.target.value); setPage(1); }}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          >
            <option value="ALL">All roles</option>
            <option value="STUDENT">STUDENT</option>
            <option value="TUTOR">TUTOR</option>
            <option value="ADMIN">ADMIN</option>
          </select>
          <button onClick={load} className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10">
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm text-white/70">
            {loading ? "Loading…" : `Total: ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <button disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15">Prev</button>
            <Chip>Page {page}</Chip>
            <button disabled={!canNext} onClick={() => setPage((p) => p + 1)} className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15">Next</button>
          </div>
        </div>

        {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-white/60">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Flags</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-white/60">No users found</td>
                </tr>
              ) : (
                items.map((u) => (
                  <tr key={u.id} className="border-b border-white/10 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.profile?.avatarUrl ? (
                          <img crossOrigin="anonymous" src={u.profile.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-white/20 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-white/10 ring-1 ring-white/20 shrink-0 flex items-center justify-center text-white/30">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                              <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                            </svg>
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-white/90">{u.name || "—"}</div>
                          <div className="text-white/60">{u.email}</div>
                          <div className="text-white/40 text-xs">{u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Chip>{u.role}</Chip>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.isBanned ? <Chip tone="red">BANNED</Chip> : <Chip tone="green">OK</Chip>}
                        {u.isVerified ? <Chip tone="purple">VERIFIED</Chip> : null}
                        {u.isFeatured ? <Chip>FEATURED</Chip> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {u.createdAt ? new Date(u.createdAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => setEditUser(u)}
                          className="rounded-lg bg-[#7269e3]/20 px-3 py-1.5 text-xs text-[#a89ef5] hover:bg-[#7269e3]/30 ring-1 ring-[#7269e3]/30"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleBan(u)}
                          className={`rounded-lg px-3 py-1.5 text-xs ring-1 ${u.isBanned ? "bg-green-500/10 text-green-300 ring-green-500/20 hover:bg-green-500/20" : "bg-red-500/10 text-red-300 ring-red-500/20 hover:bg-red-500/20"}`}
                        >
                          {u.isBanned ? "Unban" : "Ban"}
                        </button>
                        <button
                          onClick={() => invalidateTokens(u)}
                          className="rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15 ring-1 ring-white/10"
                        >
                          Invalidate tokens
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
